/**
 * FeatureGate Component - Trade Loop v2.0
 *
 * Conditionally renders content based on subscription plan features.
 * Shows upgrade prompts when features are not available.
 *
 * USAGE:
 * <FeatureGate feature="advancedAnalytics">
 *   <AdvancedAnalyticsDashboard />
 * </FeatureGate>
 *
 * <FeatureGate feature="teamCollaboration" fallback={<UpgradePrompt />}>
 *   <TeamManagementTools />
 * </FeatureGate>
 */

import React from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useNavigate } from 'react-router-dom';
import { isPlanPurchasable } from '@/config/subscription-plan-limits';

// ============================================================================
// TYPES
// ============================================================================

// Feature names aligned with FeatureFlags type from useSubscription
type FeatureName =
  | 'professionalProfile'
  | 'verifiedBadge'
  | 'unlimitedTrades'
  | 'advancedAnalytics'
  | 'partnerFavorites'
  | 'availabilityRequests'
  | 'prioritySupport'
  | 'teamCollaboration'
  | 'customIntegrations'
  | 'dedicatedAccountManager'
  | 'premiumSupport';

interface FeatureGateProps {
  /** The feature to check access for */
  feature: FeatureName;
  /** Content to show when user has access */
  children: React.ReactNode;
  /** Optional custom fallback when access denied */
  fallback?: React.ReactNode;
  /** Show default upgrade prompt (default: true) */
  showUpgradePrompt?: boolean;
  /** Custom upgrade message */
  upgradeMessage?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function FeatureGate({
  feature,
  children,
  fallback,
  showUpgradePrompt = true,
  upgradeMessage
}: FeatureGateProps) {
  const { hasFeature, planDetails, isLoading } = useSubscription();
  const navigate = useNavigate();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Check if user has the feature
  const hasAccess = hasFeature(feature);

  // User has access - render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // User doesn't have access - show fallback or upgrade prompt
  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showUpgradePrompt) {
    return null;
  }

  // Default upgrade prompt
  const featureDisplayNames: Record<FeatureName, string> = {
    professionalProfile: 'Professional Profile',
    verifiedBadge: 'Verified Badge',
    unlimitedTrades: 'Unlimited Trades',
    advancedAnalytics: 'Analytics',
    partnerFavorites: 'Partner Favorites',
    availabilityRequests: 'Availability Requests',
    prioritySupport: 'Priority Support',
    teamCollaboration: 'Team Collaboration',
    customIntegrations: 'Custom Integrations',
    dedicatedAccountManager: 'Dedicated Account Manager',
    premiumSupport: '24/7 Premium Support'
  };

  const requiredPlanNames: Record<FeatureName, string> = {
    professionalProfile: 'Pro',
    verifiedBadge: 'Pro',
    unlimitedTrades: 'Pro',
    advancedAnalytics: 'Pro',
    partnerFavorites: 'Pro',
    availabilityRequests: 'Pro',
    prioritySupport: 'Pro',
    teamCollaboration: 'Grow',
    customIntegrations: 'Grow',
    dedicatedAccountManager: 'Grow',
    premiumSupport: 'Grow'
  };

  const featureName = featureDisplayNames[feature];
  const requiredPlan = requiredPlanNames[feature];

  // A gated feature is "Coming Soon" when the plan that unlocks it cannot be bought (Card 24).
  // Keyed on purchasability rather than visibility: Grow is now VISIBLE on the plan screen, so a
  // visibility check would send the user to /plans behind a "View Plans & Upgrade" button that
  // lands on a card they cannot buy — the dead end this card exists to remove.
  const isGrowFeature = requiredPlan === 'Grow';
  const showComingSoon = isGrowFeature && !isPlanPurchasable('grow');

  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-8 text-center">
      <div className="mx-auto w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {showComingSoon ? (
            // Clock icon for coming soon
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          ) : (
            // Lock icon for upgrade required
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          )}
        </svg>
      </div>

      <h3 className="text-2xl font-bold text-gray-900 mb-2">
        {showComingSoon ? `${featureName} - Coming Soon!` : `${featureName} Available on ${requiredPlan}`}
      </h3>

      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        {showComingSoon ? (
          <>
            {featureName} will be available in a future release of Trade Loop.
            Stay tuned for updates as we continue to enhance the platform!
          </>
        ) : (
          upgradeMessage || (
            <>
              You're currently on the <span className="font-semibold">{planDetails?.name}</span> plan.
              Upgrade to <span className="font-semibold">{requiredPlan}</span> to unlock {featureName} and more premium features.
            </>
          )
        )}
      </p>

      <div className="flex gap-3 justify-center">
        {showComingSoon ? (
          // Just show go back button for coming soon features
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
          >
            Go Back
          </button>
        ) : (
          // Show both buttons for upgrade required
          <>
            <button
              onClick={() => navigate('/plans')}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              View Plans & Upgrade
            </button>

            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
            >
              Go Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FEATURE-SPECIFIC GATES (Convenience Components)
// ============================================================================

/**
 * Gate for Advanced Analytics (Core+)
 */
export function AdvancedAnalyticsGate({ children, fallback }: Omit<FeatureGateProps, 'feature'>) {
  return (
    <FeatureGate feature="advancedAnalytics" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}

/**
 * Gate for Team Collaboration (Grow only)
 */
export function TeamCollaborationGate({ children, fallback }: Omit<FeatureGateProps, 'feature'>) {
  return (
    <FeatureGate feature="teamCollaboration" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}

/**
 * Gate for Custom Integrations (Grow only)
 */
export function CustomIntegrationsGate({ children, fallback }: Omit<FeatureGateProps, 'feature'>) {
  return (
    <FeatureGate feature="customIntegrations" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}

/**
 * Gate for Professional Profile (Pro/Grow)
 */
export function ProfessionalProfileGate({ children, fallback }: Omit<FeatureGateProps, 'feature'>) {
  return (
    <FeatureGate feature="professionalProfile" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}

/**
 * Gate for Verified Badge (Pro/Grow)
 */
export function VerifiedBadgeGate({ children, fallback }: Omit<FeatureGateProps, 'feature'>) {
  return (
    <FeatureGate feature="verifiedBadge" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}

/**
 * Gate for Unlimited Trades (Pro/Grow)
 */
export function UnlimitedTradesGate({ children, fallback }: Omit<FeatureGateProps, 'feature'>) {
  return (
    <FeatureGate feature="unlimitedTrades" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}

/**
 * Gate for Partner Favorites (Pro/Grow)
 */
export function PartnerFavoritesGate({ children, fallback }: Omit<FeatureGateProps, 'feature'>) {
  return (
    <FeatureGate feature="partnerFavorites" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}
